import { $lang } from '@msdk/common'
import { Entity, FieldValue, ViewColumnDetail } from '@msdk/definitions'
import { Skeleton, Table, TableBody, TableHeader } from '@msdk/ui'
import React, { useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { CustomViewColumnDefinition, useDynamicViewContext } from '../context'
import { StyledTableCell } from '../utils'
import { PivotBodyRow } from './PivotTable.styles'
import { PivotTableHeader } from './PivotTableHeader'
import { PivotTableRow } from './PivotTableRow'

type PivotCell = {
  value: FieldValue<unknown> | null
  entity: Entity
  fieldName: string
  column: CustomViewColumnDefinition
}

export type PivotRow = {
  fieldName: string
  label: string
  values: Record<string, PivotCell>
}

export type PivotColumn = {
  id: string
  label: string | undefined
  entity: Entity
}
const DEFAULT_PIVOT_SKELETON_ROW_COUNT = 5
const DEFAULT_PIVOT_SKELETON_COLUMN_COUNT = 3

export function PivotTable() {
  const { isLoading, rows, viewModel, visibleColumns, styleMode, getColumnHeaderInfo, showExtra } =
    useDynamicViewContext()
  const [hoveredColumnId, setHoveredColumnId] = useState<string | undefined>()
  const lang = useRecoilValue($lang)

  const pivotColumnHeader = viewModel.pivotBy
  const hideEmptyRows = viewModel.pivotHideEmptyRows

  const pivotRowLabels = useMemo(
    () =>
      visibleColumns.filter(
        (column) =>
          column.fieldName !== pivotColumnHeader &&
          column.visibility &&
          (column.detail !== ViewColumnDetail.Extra || showExtra),
      ),
    [visibleColumns, pivotColumnHeader, showExtra],
  )

  const pivotColumns: PivotColumn[] = useMemo(() => {
    return (
      rows?.map((entity) => {
        const pivotField = entity.fields[pivotColumnHeader]

        if (!pivotField) {
          throw new Error(
            `Cannot pivot view by field "${pivotColumnHeader}" because entity "${entity.id}" does not contain that field.`,
          )
        }

        return {
          id: entity.id,
          label: pivotField.toStringForView(lang),
          entity,
        }
      }) ?? []
    )
  }, [rows, pivotColumnHeader, lang])

  const pivotRows: PivotRow[] = useMemo(() => {
    return pivotRowLabels.reduce<PivotRow[]>((result, label) => {
      const pivotedValue: PivotRow = {
        fieldName: label.fieldName,
        label: getColumnHeaderInfo(label).title,
        values: {},
      }
      let hasValue = false
      pivotColumns.forEach((c) => {
        const fieldValue = c.entity.fields[label.fieldName] ? c.entity.fields[label.fieldName] : null
        const displayValue = fieldValue?.toStringForView() ?? ''

        if (displayValue.trim() !== '' && !hasValue) hasValue = true

        pivotedValue.values[c.entity.id] = {
          value: fieldValue,
          entity: c.entity,
          column: label,
          fieldName: label.fieldName,
        }
      })
      if (hasValue || !hideEmptyRows) result.push(pivotedValue)
      return result
    }, [])
  }, [pivotRowLabels, pivotColumns, hideEmptyRows, getColumnHeaderInfo])

  const mode = styleMode ?? 'Default'
  const skeletonRowCount = pivotRowLabels.length || DEFAULT_PIVOT_SKELETON_ROW_COUNT
  const skeletonColumnCount = pivotColumns.length || DEFAULT_PIVOT_SKELETON_COLUMN_COUNT

  if (!isLoading && pivotColumns.length === 0) return null
  return (
    <Table>
      <TableHeader>
        <PivotTableHeader
          key={pivotColumnHeader}
          hoveredColumnId={hoveredColumnId}
          setHoveredColumnId={setHoveredColumnId}
          pivotColumns={pivotColumns}
        />
      </TableHeader>
      <TableBody>
        {!isLoading &&
          pivotRows.map((row) => (
            <PivotTableRow
              key={row.fieldName}
              row={row}
              pivotColumns={pivotColumns}
              hoveredColumnId={hoveredColumnId}
              setHoveredColumnId={setHoveredColumnId}
            />
          ))}

        {isLoading &&
          Array.from({ length: skeletonRowCount }).map((_row, rowIndex) => (
            <PivotBodyRow key={rowIndex}>
              <StyledTableCell $first $visible scope="row" size="small" styleMode={mode}>
                <Skeleton width="70%" />
              </StyledTableCell>

              {Array.from({ length: skeletonColumnCount }).map((_column, index) => (
                <StyledTableCell $visible key={index} scope="row" size="small" styleMode={mode}>
                  <Skeleton width="100%" />
                </StyledTableCell>
              ))}
            </PivotBodyRow>
          ))}
      </TableBody>
    </Table>
  )
}
